import posthog, { CaptureResult } from "posthog-js";
import { getAttribution } from "./analytics/attributionUtils";
import { Lang } from "./types";
import { devOrEmulatorEnv } from "./utils";

function devLogEvent(event: CaptureResult | null) {
  if (devOrEmulatorEnv()) {
    console.log(`PostHog dev event:`, event);
    return null; // Event == null means don't send the event to PostHog
  }
  return event;
}

const posthogEnabled = true;
const posthogApiKey = process.env.GATSBY_POSTHOG_KEY || "";
const posthogHost = process.env.GATSBY_POSTHOG_HOST;

export type AnalyticsProduct = {
  item_id: string;
  item_name: string;
  currency: string;
  price: number;
};

class PostHogAnalytics {
  private static instance: PostHogAnalytics;
  private initialized: boolean = false;

  private constructor() {
    if (typeof window !== "undefined" && posthogEnabled) {
      posthog.init(posthogApiKey, {
        api_host: posthogHost,
        persistence: "memory",
        disable_persistence: true,
        capture_pageview: false, // We'll handle this manually
        before_send: [devLogEvent],
      });
      this.initialized = true;
    }
  }

  public static getInstance(): PostHogAnalytics {
    if (!PostHogAnalytics.instance) {
      PostHogAnalytics.instance = new PostHogAnalytics();
    }
    return PostHogAnalytics.instance;
  }

  private getAttributionProperties(): Record<string, unknown> {
    const attribution = getAttribution();
    return {
      source: attribution.source,
      medium: attribution.medium,
      campaign: attribution.campaign,
      content: attribution.content,
      term: attribution.term,
      initialPath: attribution.initialPath,
      referrer: attribution.referrer,
    };
  }

  public pageView(url: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) return;

    posthog.capture("$pageview", {
      url,
      ...this.getAttributionProperties(),
      ...properties,
    });
  }

  public track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) return;

    posthog.capture(eventName, {
      ...this.getAttributionProperties(),
      ...properties,
    });
  }

  public identify(
    distinctId: string,
    properties?: Record<string, unknown>,
  ): void {
    if (!this.initialized) return;

    posthog.identify(distinctId, properties);
  }

  public reset(): void {
    if (!this.initialized) return;

    posthog.reset();
  }

  // Convenience methods for funnel tracking
  public trackExamplePhotoClicked(): void {
    this.track("example_photo_clicked");
  }

  public trackDemoStarted(): void {
    this.track("demo_started");
  }

  public trackSettingsOpened(): void {
    this.track("settings_opened");
  }

  public trackSaveModalOpened(): void {
    this.track("save_modal_opened");
  }

  public trackBackgroundRemoved(method: "imgly" | "mediapipe"): void {
    this.track("background_removed", { method });
  }

  public trackBeginCheckout(lang: Lang, product: AnalyticsProduct): void {
    this.track("begin_checkout", {
      lang,
      value: product.price,
      currency: product.currency,
      item_id: product.item_id,
      item_name: product.item_name,
    });
  }

  public trackPurchase(
    lang: Lang,
    product: AnalyticsProduct,
    transactionId: string,
  ): void {
    this.track("purchase", {
      lang,
      value: product.price,
      currency: product.currency,
      item_id: product.item_id,
      item_name: product.item_name,
      transaction_id: transactionId,
    });
  }

  public trackEarlyAccessSignup(): void {
    this.track("early_access_signup");
  }

  public trackPhotoSaved(params: {
    photoStandard: string;
    backgroundRemoved: boolean;
    lang: Lang;
    isDemoImage: boolean;
    saveTab: "quick" | "layout";
    format: "png" | "jpg" | "pdf" | "jpg-print" | "pdf-print";
    photosPerSheet?: number;
  }): void {
    this.track("photo_saved", params);
  }
}

export const posthogAnalytics = PostHogAnalytics.getInstance();
